/**
 * iter148 + iter149 — Financial Center Modal.
 *
 * THE single financial editing surface. Manages:
 *   • Original Proposal Amount (read-only history)
 *   • Current Contract Amount (editable master value)
 *   • Split Allocation (auto-recalc or manual per-party edits)
 *   • Party Payments Recording (immutable ledger with adjustments)
 *   • Financial History timeline
 *
 * Tracker + Dashboard read the values this modal produces — they never
 * store their own copy.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError, inr, fmtDate } from "@/lib/utils";
import {
  X, IndianRupee, Loader2, AlertTriangle, History, Users, Check,
  Plus, Trash2, Receipt, CalendarClock, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import RecordPaymentModal from "@/components/RecordPaymentModal";

const DEFAULT_MODE_LABELS = {
  cash: "Cash", upi: "UPI", bank: "Bank Transfer",
  cheque: "Cheque", card: "Card", other: "Other",
};

export default function FinancialCenterModal({ open, booking, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [splitMode, setSplitMode] = useState("auto");   // "auto" | "manual"
  const [manualParties, setManualParties] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirming, setConfirming] = useState(false);

  // Payments ledger (fetched separately so it can refresh on every action).
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [payFor, setPayFor] = useState(null);  // party object for the Record Payment sub-modal
  const [editFor, setEditFor] = useState(null);   // {receipt, party} for Edit sub-modal
  const [voidFor, setVoidFor] = useState(null);   // {receipt, party} for Void confirm

  const original = Number(booking?.original_proposal_amount ?? booking?.quote?.grand_total ?? 0);
  const current  = Number(booking?.current_contract_amount ?? booking?.quote?.grand_total ?? 0);
  const splitEnabled = !!booking?.payment_split?.enabled;
  const history = booking?.financial_history || [];

  const loadLedger = useCallback(async () => {
    if (!booking?.id) return;
    setLedgerLoading(true);
    try {
      const { data } = await api.get(`/bookings/${booking.id}/payments`);
      setLedger(data);
    } catch { /* ignore — non-critical */ }
    finally { setLedgerLoading(false); }
  }, [booking?.id]);

  useEffect(() => {
    if (!open || !booking) return;
    setAmount(String(current || ""));
    setReason("");
    setSplitMode("auto");
    // Seed manual parties from existing split so users can tweak.
    const parties = booking?.payment_split?.parties || [];
    setManualParties(parties.map((p) => ({
      id: p.id, name: p.name || "", relationship: p.relationship || "",
      amount: String(p.amount ?? ""),
    })));
    setErr("");
    setConfirming(false);
    loadLedger();
  }, [open, booking, current, loadLedger]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) {
        if (confirming) setConfirming(false);
        else if (voidFor) setVoidFor(null);
        else if (editFor) setEditFor(null);
        else if (payFor) setPayFor(null);
        else onClose?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, confirming, payFor, editFor, voidFor, onClose]);

  const newAmount = useMemo(() => {
    const n = Number(amount);
    return isFinite(n) && n >= 0 ? n : NaN;
  }, [amount]);
  const delta = isFinite(newAmount) ? newAmount - current : 0;
  const changed = isFinite(newAmount) && Math.abs(delta) > 0.01;

  // Manual-split validation.
  const manualSum = useMemo(
    () => manualParties.reduce((s, p) => s + (Number(p.amount) || 0), 0),
    [manualParties],
  );
  const manualBalanced = Math.abs(manualSum - newAmount) < 1.0;
  const manualActive = splitEnabled && changed && splitMode === "manual";
  const canSave =
    changed && reason.trim().length > 0 && !busy &&
    (!manualActive || manualBalanced);

  const patchManualParty = (idx, patch) => {
    setManualParties((prev) => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  };
  const addManualParty = () => {
    setManualParties((prev) => [
      ...prev,
      { id: null, name: `Party ${prev.length + 1}`, relationship: "Custom", amount: "" },
    ]);
  };
  const removeManualParty = (idx) => {
    setManualParties((prev) => prev.filter((_, i) => i !== idx));
  };

  const doSave = async () => {
    setBusy(true);
    setErr("");
    try {
      const payload = {
        amount: newAmount,
        reason: reason.trim(),
      };
      if (splitEnabled) {
        if (splitMode === "manual") {
          payload.parties = manualParties.map((p) => ({
            id: p.id || undefined,
            name: (p.name || "").trim() || "Party",
            relationship: p.relationship || "",
            amount: Number(p.amount) || 0,
          }));
        } else {
          payload.recalculate_split = true;
        }
      }
      const { data } = await api.patch(`/bookings/${booking.id}/contract-amount`, payload);
      toast.success(
        splitEnabled && data.recalculated_split
          ? (splitMode === "manual" ? "Contract updated · split allocated manually" : "Contract updated · split re-balanced")
          : "Contract amount updated"
      );
      await loadLedger();
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open || !booking) return null;

  const ledgerParties = ledger?.parties || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40 kb-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Financial Center"
    >
      <div
        className="bg-white w-full max-w-lg h-full flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="financial-center-modal"
      >
        <div className="flex items-center justify-between px-6 h-16 border-b border-stone-200 shrink-0">
          <div className="flex items-center gap-3">
            <IndianRupee className="w-5 h-5 text-emerald-700" strokeWidth={1.6} aria-hidden="true" />
            <div>
              <h2 className="font-heading text-lg font-semibold leading-tight">Financial Center</h2>
              <div className="text-[11px] uppercase tracking-wider text-stone-500">
                {booking.couple_name || booking.client_name}
              </div>
            </div>
          </div>
          <button
            className="btn-ghost p-2 rounded kb-tap"
            onClick={onClose}
            aria-label="Close financial center"
            data-testid="financial-center-close"
          >
            <X className="w-5 h-5" strokeWidth={1.6} aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Original + Current side-by-side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="card p-3 bg-stone-50 border-stone-200">
              <div className="editorial-eyebrow flex items-center gap-1"><History className="w-3 h-3" /> Original Proposal</div>
              <div className="mt-1 font-heading text-xl text-stone-700 font-mono" data-testid="financial-center-original">
                {inr(original)}
              </div>
              <div className="text-[10px] tracking-widest uppercase text-stone-400 mt-0.5">read-only</div>
            </div>
            <div className="card p-3 bg-emerald-50/40 border-emerald-200">
              <div className="editorial-eyebrow flex items-center gap-1 text-emerald-700"><IndianRupee className="w-3 h-3" /> Current Contract</div>
              <div className="mt-1 font-heading text-xl text-emerald-800 font-mono" data-testid="financial-center-current">
                {inr(current)}
              </div>
              <div className="text-[10px] tracking-widest uppercase text-emerald-700/70 mt-0.5">master value</div>
            </div>
          </div>

          {/* Editable amount */}
          <div>
            <label className="editorial-eyebrow block mb-1" htmlFor="fc-amount">New Contract Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none">₹</span>
              <input
                id="fc-amount"
                type="number"
                min={0}
                className="input pl-6 text-right font-mono text-lg"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                data-testid="financial-center-amount"
              />
            </div>
            {changed && (
              <div className={`mt-1 text-xs ${delta > 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="financial-center-delta">
                {delta > 0 ? "+" : ""}{inr(delta)} ({delta > 0 ? "increase" : "decrease"})
              </div>
            )}
          </div>

          {/* Reason */}
          <div>
            <label className="editorial-eyebrow block mb-1" htmlFor="fc-reason">Reason for Change <span className="text-rose-700">*</span></label>
            <textarea
              id="fc-reason"
              rows={2}
              className="input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Added premium album, family requested extra day, discount for early booking…"
              maxLength={500}
              data-testid="financial-center-reason"
            />
          </div>

          {/* iter149 — Split Allocation section (only if split enabled AND amount changed) */}
          {splitEnabled && changed && (
            <div className="card p-3 bg-sky-50/60 border-sky-200" data-testid="financial-center-split-choice">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-sky-700" strokeWidth={2.2} aria-hidden="true" />
                <div className="font-heading font-semibold text-sm text-sky-900">Split Allocation</div>
              </div>
              <div className="space-y-2 text-sm">
                <label className="flex items-start gap-2 cursor-pointer" data-testid="financial-center-split-recalc-yes">
                  <input
                    type="radio"
                    name="split-mode"
                    checked={splitMode === "auto"}
                    onChange={() => setSplitMode("auto")}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-semibold text-stone-800">Recalculate Automatically</div>
                    <div className="text-xs text-stone-500">Scale each party's share proportionally to match the new total.</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer" data-testid="financial-center-split-recalc-no">
                  <input
                    type="radio"
                    name="split-mode"
                    checked={splitMode === "manual"}
                    onChange={() => setSplitMode("manual")}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-semibold text-stone-800">Edit Manually</div>
                    <div className="text-xs text-stone-500">Assign amounts party-by-party. Total must equal the new contract amount.</div>
                  </div>
                </label>
              </div>

              {splitMode === "manual" && (
                <div className="mt-3 space-y-2" data-testid="financial-center-manual-parties">
                  {manualParties.map((p, i) => (
                    <div key={p.id || `new-${i}`} className="grid grid-cols-12 gap-2 items-center" data-testid={`fc-manual-party-${i}`}>
                      <input
                        type="text"
                        value={p.name}
                        onChange={(e) => patchManualParty(i, { name: e.target.value })}
                        className="input py-1 text-xs col-span-6"
                        placeholder="Party name"
                        data-testid={`fc-manual-name-${i}`}
                      />
                      <div className="relative col-span-5">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">₹</span>
                        <input
                          type="number"
                          min={0}
                          value={p.amount}
                          onChange={(e) => patchManualParty(i, { amount: e.target.value })}
                          className="input py-1 pl-6 text-right font-mono text-xs"
                          placeholder="0"
                          data-testid={`fc-manual-amount-${i}`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeManualParty(i)}
                        className="col-span-1 p-1 text-stone-400 hover:text-rose-700"
                        title="Remove party"
                        data-testid={`fc-manual-remove-${i}`}
                        disabled={manualParties.length <= 1}
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addManualParty}
                    className="text-xs uppercase tracking-widest text-sky-700 hover:text-sky-900 inline-flex items-center gap-1"
                    data-testid="fc-manual-add-party"
                  >
                    <Plus className="w-3 h-3" strokeWidth={2.4} /> Add Party
                  </button>
                  <div className={`mt-2 flex items-baseline justify-between text-xs px-2 py-1.5 rounded ${manualBalanced ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`} data-testid="fc-manual-allocation-total">
                    <span className="uppercase tracking-widest font-semibold">Allocated Total</span>
                    <span className="font-mono">
                      {inr(manualSum)} / {inr(newAmount || 0)}
                      {!manualBalanced && (
                        <span className="ml-2 font-semibold">
                          ({manualSum > newAmount ? "-" : "+"}{inr(Math.abs(manualSum - newAmount))} off)
                        </span>
                      )}
                    </span>
                  </div>
                  {!manualBalanced && (
                    <div className="text-[11px] text-rose-700" data-testid="fc-manual-error">
                      Save disabled — Allocated Total must equal the new Current Contract amount.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* iter149 + iter194.1 — Client Payments section. Always
             visible for every booking. Non-split flows show a single
             "Client" bucket (synthetic party). Split-enabled bookings
             additionally show the bride/groom cards below. */}
          <div className="card p-3 bg-white border-stone-200" data-testid="financial-center-payments">
            <div className="flex items-center gap-2 mb-3">
              <Receipt className="w-4 h-4 text-emerald-700" strokeWidth={2.2} aria-hidden="true" />
              <div className="font-heading font-semibold text-sm">
                {splitEnabled ? "Party Payments" : "Client Payments"}
              </div>
              {ledger && (
                <span className="ml-auto text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                  Collected {inr(ledger.collected)} / {inr(ledger.contract_amount)} ({ledger.collection_percent}%)
                </span>
              )}
            </div>
            {ledgerLoading && !ledger ? (
              <div className="space-y-2">
                <div className="kb-skeleton h-20 rounded-lg" />
                <div className="kb-skeleton h-20 rounded-lg" />
              </div>
            ) : (
              <ul className="space-y-3">
                {ledgerParties.map((p) => (
                  <li key={p.id} className={`border rounded-lg p-3 ${p.synthetic ? "border-emerald-200 bg-emerald-50/30" : "border-stone-200 bg-stone-50/50"}`} data-testid={`fc-party-card-${p.id}`}>
                    <div className="flex items-baseline justify-between mb-2 gap-2">
                      <div className="min-w-0">
                        <div className="font-heading font-semibold text-sm truncate">{p.name}</div>
                        {p.relationship && <div className="text-[10px] text-stone-500">{p.relationship}</div>}
                      </div>
                      <button
                        type="button"
                        onClick={() => setPayFor(p)}
                        className="btn btn-primary btn-xs kb-tap"
                        data-testid={`fc-record-payment-${p.id}`}
                        disabled={busy}
                      >
                        <Plus className="w-3 h-3" strokeWidth={2.4} /> Record Payment
                      </button>
                    </div>
                      <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Contract</div>
                          <div className="font-mono text-stone-800" data-testid={`fc-party-assigned-${p.id}`}>{inr(p.assigned)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Received</div>
                          <div className="font-mono text-emerald-800" data-testid={`fc-party-paid-${p.id}`}>{inr(p.paid)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-rose-700 font-semibold">Pending</div>
                          <div className="font-mono text-rose-800" data-testid={`fc-party-pending-${p.id}`}>{inr(p.pending)}</div>
                        </div>
                      </div>
                      <div className="mb-2">
                        <div className="flex items-baseline justify-between mb-0.5">
                          <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{p.percent_paid}% paid</span>
                        </div>
                        <div className="h-1.5 w-full bg-stone-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all"
                            style={{ width: `${Math.min(100, Math.max(0, Number(p.percent_paid) || 0))}%` }}
                          />
                        </div>
                      </div>
                    {p.receipts && p.receipts.length > 0 && (
                      <ul className="mt-2 space-y-1 text-[11px] max-h-40 overflow-y-auto pr-1" data-testid={`fc-party-history-${p.id}`}>
                        {p.receipts.slice().reverse().map((r) => (
                          <li
                            key={r.id}
                            className={`px-2 py-1.5 rounded ${r.is_adjustment ? "bg-amber-50 border border-amber-200" : "bg-white border border-stone-100"}`}
                          >
                            <div className="flex items-baseline justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <span className={`font-mono font-semibold ${r.amount < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                                    {r.amount < 0 ? "-" : "+"}{inr(Math.abs(r.amount))}
                                  </span>
                                  <span className="text-stone-400 uppercase tracking-widest text-[9px]">
                                    {r.is_adjustment ? (r.edit_action === "void" ? "Voided" : r.edit_action === "edit" ? "Edit" : "Adjustment")
                                      : (DEFAULT_MODE_LABELS[r.mode] || r.mode)}
                                  </span>
                                </div>
                                {r.reference && <div className="text-stone-500 truncate">Ref · {r.reference}</div>}
                                {r.notes && <div className="text-stone-500 truncate italic">{r.notes}</div>}
                              </div>
                              <div className="text-stone-400 shrink-0 tabular-nums text-[10px]">
                                {r.payment_date ? fmtDate(r.payment_date.slice(0, 10)) : ""}
                              </div>
                            </div>
                            {!r.is_adjustment && (
                              <div className="flex items-center gap-2 mt-1 pt-1 border-t border-stone-100">
                                <button
                                  type="button"
                                  className="text-[10px] uppercase tracking-widest text-stone-500 hover:text-emerald-700 font-semibold"
                                  onClick={() => setEditFor({ receipt: r, party: p })}
                                  data-testid={`fc-payment-edit-${r.id}`}
                                >
                                  Edit
                                </button>
                                <span className="text-stone-300">·</span>
                                <button
                                  type="button"
                                  className="text-[10px] uppercase tracking-widest text-stone-500 hover:text-rose-700 font-semibold"
                                  onClick={() => setVoidFor({ receipt: r, party: p })}
                                  data-testid={`fc-payment-void-${r.id}`}
                                >
                                  Void
                                </button>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
                {ledgerParties.length === 0 && (
                  <li className="text-xs text-stone-400 italic text-center py-4">
                    No client bucket configured — try re-opening this booking.
                  </li>
                )}
              </ul>
            )}
          </div>

          {/* Financial History */}
          {history.length > 0 && (
            <div>
              <div className="editorial-eyebrow flex items-center gap-1 mb-2"><History className="w-3 h-3" /> Financial History</div>
              <ul className="space-y-1 max-h-40 overflow-y-auto" data-testid="financial-center-history">
                {history.slice().reverse().map((h, i) => (
                  <li key={i} className="text-xs bg-stone-50 border border-stone-100 rounded px-2 py-1.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-mono text-stone-700">
                        {inr(h.old_amount)} → <span className="text-stone-900 font-semibold">{inr(h.new_amount)}</span>
                      </span>
                      <span className="text-stone-400 text-[10px] tracking-wider">
                        {h.at ? fmtDate(h.at.slice(0, 10)) : ""}
                      </span>
                    </div>
                    {h.reason && <div className="text-stone-600 mt-0.5 truncate">{h.reason}</div>}
                    <div className="text-stone-400 text-[10px] mt-0.5">
                      {h.user_email}
                      {h.recalculated_split ? " · split changed" : ""}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {err && (
            <div className="card p-3 bg-rose-50 border-rose-200 flex items-start gap-2" role="alert">
              <AlertTriangle className="w-4 h-4 text-rose-700 mt-0.5 shrink-0" strokeWidth={2} />
              <div className="text-xs text-rose-900">{err}</div>
            </div>
          )}
        </div>

        {/* Footer: confirm-first pattern */}
        <div className="px-6 py-4 border-t border-stone-200 bg-stone-50/60 shrink-0">
          {!confirming ? (
            <div className="flex items-center justify-end gap-2">
              <button className="btn btn-outline" onClick={onClose} data-testid="financial-center-cancel">
                Close
              </button>
              <button
                className="btn btn-primary"
                disabled={!canSave}
                onClick={() => setConfirming(true)}
                data-testid="financial-center-review"
              >
                Review changes <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div data-testid="financial-center-confirm">
              <div className="editorial-eyebrow mb-2">Confirm — these values will update</div>
              <ul className="text-xs space-y-1.5 mb-3">
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-emerald-700 shrink-0" strokeWidth={2.4} />
                  <span>Contract Amount <span className="font-mono">{inr(current)} → {inr(newAmount)}</span></span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-emerald-700 shrink-0" strokeWidth={2.4} />
                  <span>Payment Tracker · Pending · Milestones · Client Tracker</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-emerald-700 shrink-0" strokeWidth={2.4} />
                  <span>Invoice Amount · Dashboard Revenue</span>
                </li>
                {splitEnabled && (
                  <li className="flex items-center gap-2">
                    <Check className="w-3 h-3 text-emerald-700 shrink-0" strokeWidth={2.4} />
                    <span>
                      Split Payment: {splitMode === "manual" ? "manual allocation" : "recalculated proportionally"}
                      <span className="text-emerald-800/80 ml-1">— existing payments untouched</span>
                    </span>
                  </li>
                )}
                <li className="flex items-center gap-2 text-stone-500">
                  <Check className="w-3 h-3 text-stone-400 shrink-0" strokeWidth={2.4} />
                  <span>Original Proposal Amount stays <span className="font-mono">{inr(original)}</span></span>
                </li>
              </ul>
              <div className="flex items-center justify-end gap-2">
                <button
                  className="btn btn-outline"
                  onClick={() => setConfirming(false)}
                  disabled={busy}
                  data-testid="financial-center-back"
                >
                  Back
                </button>
                <button
                  className="btn btn-primary"
                  onClick={doSave}
                  disabled={busy}
                  data-testid="financial-center-save"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={2.2} />}
                  Save & Cascade
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <RecordPaymentModal
        open={!!payFor}
        booking={booking}
        party={payFor}
        onClose={() => setPayFor(null)}
        onSaved={async () => {
          setPayFor(null);
          await loadLedger();
          onSaved?.({ ledger_refreshed: true });
        }}
      />

      {/* iter194.1 — Edit Payment inline modal */}
      <EditPaymentInline
        open={!!editFor}
        booking={booking}
        target={editFor}
        onClose={() => setEditFor(null)}
        onSaved={async () => {
          setEditFor(null);
          await loadLedger();
          onSaved?.({ ledger_refreshed: true });
        }}
      />

      {/* iter194.1 — Void Payment confirmation */}
      <VoidPaymentConfirm
        open={!!voidFor}
        booking={booking}
        target={voidFor}
        onClose={() => setVoidFor(null)}
        onSaved={async () => {
          setVoidFor(null);
          await loadLedger();
          onSaved?.({ ledger_refreshed: true });
        }}
      />
    </div>
  );
}


// ---------------------------------------------------------------------
// iter194.1 — Edit Payment inline sub-modal.
// ---------------------------------------------------------------------
function EditPaymentInline({ open, booking, target, onClose, onSaved }) {
  const receipt = target?.receipt;
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [mode, setMode] = useState("cash");
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !receipt) return;
    setAmount(String(receipt.amount ?? ""));
    setPaymentDate((receipt.payment_date || "").slice(0, 10));
    setMode(receipt.mode || "cash");
    setReference(receipt.reference || "");
    setReason("");
    setErr("");
  }, [open, receipt]);

  if (!open || !receipt) return null;
  const amt = Number(amount) || 0;
  const changed = Math.abs(amt - Number(receipt.amount)) > 0.005
    || paymentDate !== (receipt.payment_date || "").slice(0, 10)
    || mode !== receipt.mode
    || reference !== (receipt.reference || "");
  const amountChanged = Math.abs(amt - Number(receipt.amount)) > 0.005;
  const canSave = changed && (!amountChanged || reason.trim().length > 0) && !busy;

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/bookings/${booking.id}/payments/${receipt.id}`, {
        amount: amt,
        payment_date: paymentDate,
        mode,
        reference: reference.trim(),
        reason: reason.trim() || "Payment edited",
      });
      toast.success("Payment updated");
      onSaved?.();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
         onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden"
           onClick={(e) => e.stopPropagation()} data-testid="edit-payment-modal">
        <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-amber-700" strokeWidth={2} />
            <div>
              <div className="font-heading font-semibold">Edit Payment</div>
              <div className="text-[11px] uppercase tracking-widest text-stone-500">
                {inr(receipt.amount)} · {(receipt.payment_date || "").slice(0, 10)}
              </div>
            </div>
          </div>
          <button className="btn-ghost p-1 rounded" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="editorial-eyebrow block mb-1">New Amount <span className="text-rose-700">*</span></label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none">₹</span>
              <input type="number" min={0} className="input pl-6 text-right font-mono"
                     value={amount} onChange={(e) => setAmount(e.target.value)}
                     data-testid="edit-payment-amount" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="editorial-eyebrow block mb-1">Payment Date</label>
              <input type="date" className="input" value={paymentDate}
                     onChange={(e) => setPaymentDate(e.target.value)}
                     data-testid="edit-payment-date" />
            </div>
            <div>
              <label className="editorial-eyebrow block mb-1">Mode</label>
              <select className="input" value={mode} onChange={(e) => setMode(e.target.value)}
                      data-testid="edit-payment-mode">
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="bank">Bank Transfer</option>
                <option value="cheque">Cheque</option>
                <option value="card">Card</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div>
            <label className="editorial-eyebrow block mb-1">Reference / UTR</label>
            <input type="text" className="input" value={reference}
                   onChange={(e) => setReference(e.target.value)}
                   maxLength={120} data-testid="edit-payment-reference" />
          </div>
          {amountChanged && (
            <div>
              <label className="editorial-eyebrow block mb-1">
                Reason for amount change <span className="text-rose-700">*</span>
              </label>
              <input type="text" className="input" value={reason}
                     onChange={(e) => setReason(e.target.value)}
                     placeholder="e.g. Corrected UPI amount"
                     maxLength={200} data-testid="edit-payment-reason" />
            </div>
          )}
          {err && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>
          )}
          <div className="text-[10px] text-stone-400 italic">
            Original payment is preserved as an audit trail — a signed adjustment
            row will be added rather than mutating history.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-stone-200 bg-stone-50/60 flex items-center justify-end gap-2">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" disabled={!canSave} onClick={save}
                  data-testid="edit-payment-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={2.2} />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------
// iter194.1 — Void Payment confirmation.
// ---------------------------------------------------------------------
function VoidPaymentConfirm({ open, booking, target, onClose, onSaved }) {
  const receipt = target?.receipt;
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) { setReason(""); setErr(""); }
  }, [open]);

  if (!open || !receipt) return null;
  const doVoid = async () => {
    setBusy(true); setErr("");
    try {
      await api.delete(`/bookings/${booking.id}/payments/${receipt.id}`, {
        params: { reason: reason.trim() || "Payment voided" },
      });
      toast.success(`Voided ${inr(receipt.amount)}`);
      onSaved?.();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
         onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl overflow-hidden"
           onClick={(e) => e.stopPropagation()} data-testid="void-payment-modal">
        <div className="px-5 py-4 border-b border-stone-200 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-rose-700" strokeWidth={2} />
          <div>
            <div className="font-heading font-semibold">Remove this payment?</div>
            <div className="text-[11px] uppercase tracking-widest text-stone-500">
              {inr(receipt.amount)} · {(receipt.payment_date || "").slice(0, 10)}
            </div>
          </div>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="text-xs text-stone-600">
            A reversal entry will be added — the original payment stays in
            history for audit. Received will decrease by {inr(receipt.amount)}
            and Pending will increase by {inr(receipt.amount)} everywhere.
          </div>
          <div>
            <label className="editorial-eyebrow block mb-1">Reason (optional)</label>
            <input type="text" className="input" value={reason}
                   onChange={(e) => setReason(e.target.value)}
                   maxLength={200} placeholder="e.g. Duplicate entry"
                   data-testid="void-payment-reason" />
          </div>
          {err && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-stone-200 bg-stone-50/60 flex items-center justify-end gap-2">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn bg-rose-700 text-white hover:bg-rose-800 disabled:opacity-50"
                  disabled={busy} onClick={doVoid}
                  data-testid="void-payment-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" strokeWidth={2.2} />}
            Remove Payment
          </button>
        </div>
      </div>
    </div>
  );
}
